import { AuthenticationDetails, CognitoUser, CognitoUserSession } from 'amazon-cognito-identity-js';
import { AuthStage, cookieStorage } from '.';
import { Method, queryData } from '../../../utilities/fetch';
import type { ISignInResponse } from 'api-paths';
import { shouldShowMFASetup, userMFAEnabled } from './mfa-setup/mfa';
import { clearListOfDevicesAsync } from './clearListOfDevices';
import { clearStaleDeviceCookies } from './clearCognitoCookies';

interface CheckMfaEnforcementParams {
  session: CognitoUserSession;
  user: CognitoUser;
  mfaEnabledInConfig: boolean;
  username: string;
  appClient: string;
  apiUrl: string;
  setAuthStage: (stage: AuthStage) => void;
  setRequiresUpdatedTermsAcceptance?: (value: boolean) => void;
  onAuthCheckError?: (error: Error) => void;
}

export async function checkMfaEnforcement({
  session,
  user,
  mfaEnabledInConfig,
  username,
  appClient,
  apiUrl,
  setAuthStage,
  setRequiresUpdatedTermsAcceptance,
  onAuthCheckError,
}: CheckMfaEnforcementParams): Promise<void> {
  try {
    const userResult = await queryData<{ clientId: string; token: string; username: string }, ISignInResponse>({
      uri: `/users/whoami`,
      idToken: session.getIdToken().getJwtToken(),
      domain: apiUrl,
      method: Method.Post,
      body: { clientId: appClient, token: session.getRefreshToken().getToken(), username },
      // Absorb brief 5xx/network blips with bounded exponential backoff before failing.
      retry: true,
    });
    // Handle error response (ErrorResponseBody has 'error' property, Lambda errors have 'errorType')
    if (!userResult || (typeof userResult === 'object' && ('error' in userResult || 'errorType' in userResult))) {
      // Don't auto-route to MFA setup on a whoami error — during a degraded backend that would
      // cascade every user into a half-broken setup flow. Hand off to the caller and bounce to login.
      onAuthCheckError?.(new Error('whoami returned an error response'));
      setAuthStage(AuthStage.LOGIN);
      return;
    }
    const { isAgentUser = false, agencyMfaEnabled = false, requiresUpdatedTermsAcceptance = false } = userResult;
    if (requiresUpdatedTermsAcceptance) {
      setRequiresUpdatedTermsAcceptance?.(true);
    }
    if (!shouldShowMFASetup(mfaEnabledInConfig, isAgentUser, agencyMfaEnabled)) {
      setAuthStage(requiresUpdatedTermsAcceptance ? AuthStage.AGENT_UPDATED_TERMS : AuthStage.NONE);
      return;
    }
    const userMfaEnabled = await userMFAEnabled(user);
    if (!userMfaEnabled) {
      // if MFA is not setup, we clear the list of devices.
      // This allows the device list to be cleared as part of the reset MFA flow,
      // triggered by institution administrators.
      await clearListOfDevicesAsync(user, username, appClient);
      setAuthStage(AuthStage.MFA_SETUP);
      return;
    }
    // MFA already set up — check if T&Cs still need accepting
    setAuthStage(requiresUpdatedTermsAcceptance ? AuthStage.AGENT_UPDATED_TERMS : AuthStage.NONE);
  } catch (error) {
    // Transient failure (5xx / network / timeout) even after the fetch-layer retries.
    // Failing into MFA_SETUP would cascade every user into a broken setup flow during an outage,
    // so surface the error to the caller and bounce back to login instead.
    onAuthCheckError?.(error instanceof Error ? error : new Error(String(error)));
    setAuthStage(AuthStage.LOGIN);
  }
}

interface Params {
  mfaEnabledInConfig: boolean;
  username: string;
  password: string;
  user: CognitoUser;
  appClient: string;
  apiUrl: string;
  uniCode: string;
  setAuthStage: (stage: AuthStage) => void;
  setPassword: (password: string) => void;
  setIsLoading: (loading: boolean) => void;
  setRequiresUpdatedTermsAcceptance?: (value: boolean) => void;
  onAuthCheckError?: (error: Error) => void;
}

export default async function onLoginClick(params: Params): Promise<CognitoUserSession | undefined> {
  const {
    mfaEnabledInConfig,
    uniCode,
    username,
    password,
    user,
    setAuthStage,
    setPassword,
    setIsLoading,
    appClient,
    apiUrl,
    setRequiresUpdatedTermsAcceptance,
    onAuthCheckError,
  } = params;

  const onSuccessLogin = async (result: CognitoUserSession): Promise<CognitoUserSession> => {
    cookieStorage.setItem('username', username);
    cookieStorage.setItem('appClient', appClient); // for NextJS app
    cookieStorage.setItem('apiUrl', apiUrl); // for NextJS app
    cookieStorage.setItem('uni', uniCode); // for NextJS app

    await checkMfaEnforcement({
      session: result,
      user,
      mfaEnabledInConfig,
      username,
      appClient,
      apiUrl,
      setAuthStage,
      setRequiresUpdatedTermsAcceptance,
      onAuthCheckError,
    });

    // this indicates we are redirecting from a forgot-password login.
    const clearPendingDevices = cookieStorage.getItem('devicesPendingClear') === 'true';
    if (clearPendingDevices) {
      cookieStorage.removeItem('devicesPendingClear');
      await clearListOfDevicesAsync(user, username, appClient);
    }

    return result;
  };

  const newPasswordRequired = (): undefined => {
    setAuthStage(AuthStage.NEW_PASSWORD);
    setPassword('');
    setIsLoading(false);
    return undefined;
  };

  const mfaRequired = (): undefined => {
    setAuthStage(AuthStage.SMS_CODE);
    cookieStorage.setItem('username', username);
    cookieStorage.setItem('appClient', appClient); // for NextJS app
    cookieStorage.setItem('apiUrl', apiUrl); // for NextJS app
    cookieStorage.setItem('uni', uniCode); // for NextJS app
    return undefined;
  };

  const totpRequired = (): undefined => {
    setAuthStage(AuthStage.TOTP_CODE);
    cookieStorage.setItem('username', username);
    cookieStorage.setItem('appClient', appClient); // for NextJS app
    cookieStorage.setItem('apiUrl', apiUrl); // for NextJS app
    cookieStorage.setItem('uni', uniCode); // for NextJS app
    return undefined;
  };

  const customChallenge = (): undefined => {
    return undefined;
  };

  const mfaSetup = (): undefined => {
    setAuthStage(AuthStage.MFA_SETUP);
    cookieStorage.setItem('username', username);
    cookieStorage.setItem('appClient', appClient); // for NextJS app
    cookieStorage.setItem('apiUrl', apiUrl); // for NextJS app
    cookieStorage.setItem('uni', uniCode); // for NextJS app
    return undefined;
  };

  const selectMFAType = (): undefined => {
    setAuthStage(AuthStage.MFA_SETUP);
    cookieStorage.setItem('username', username);
    cookieStorage.setItem('appClient', appClient); // for NextJS app
    cookieStorage.setItem('apiUrl', apiUrl); // for NextJS app
    cookieStorage.setItem('uni', uniCode); // for NextJS app
    return undefined;
  };

  const authenticationDetails = new AuthenticationDetails({
    Username: username,
    Password: password,
  });

  const authenticateUserAsync = (
    user: CognitoUser,
    authenticationDetails: AuthenticationDetails,
    retry?: boolean,
  ): Promise<CognitoUserSession | undefined> => {
    return new Promise((resolve, reject) => {
      user.authenticateUser(authenticationDetails, {
        onSuccess: async (result: CognitoUserSession) => {
          resolve(await onSuccessLogin(result));
        },
        onFailure: async (reason: Error & { code: string }) => {
          setIsLoading(false);
          // user.authenticateUser() fails when a user has an apostrophe in their username. The authentication API
          // request is successful and the session data is updated but the Device API request made after fails
          if (reason?.code === 'InvalidParameterException' && username.includes("'")) {
            const session = user.getSignInUserSession();
            if (session) {
              resolve(await onSuccessLogin(session));
            } else {
              reject(new Error('Unable to get session'));
            }
          } else if (reason?.code === 'ResourceNotFoundException' || reason?.message === 'Device does not exist.') {
            if (retry) {
              reject(reason);
              return;
            }
            // The local device key cookie is stale — the server-side device record no longer exists.
            // Clear the stale device cookies and retry auth, which will fall through to MFA normally.
            clearStaleDeviceCookies(cookieStorage, appClient, username);
            try {
              resolve(await authenticateUserAsync(user, authenticationDetails, true));
            } catch (retryError) {
              reject(retryError);
            }
          } else {
            reject(reason);
          }
        },
        newPasswordRequired: () => {
          // console.log('on newPasswordRequired!');
          resolve(newPasswordRequired());
        },
        mfaRequired: () => {
          // console.log('on mfaRequired!');
          resolve(mfaRequired());
        },
        totpRequired: () => {
          // console.log('on totpRequired!');
          resolve(totpRequired());
        },
        customChallenge: () => {
          // console.log('on customChallenge!');
          resolve(customChallenge());
        },
        mfaSetup: () => {
          // console.log('on mfaSetup!');
          resolve(mfaSetup());
        },
        selectMFAType: () => {
          // console.log('on selectMFAType!');
          resolve(selectMFAType());
        },
      });
    });
  };

  return authenticateUserAsync(user, authenticationDetails);
}
